import React, { useState } from "react";
import { Card, Row, Col, Button, Drawer, Typography, Divider, Table, Tag, Space, Collapse, Tooltip } from "antd";
import { QuestionCircleOutlined, ThunderboltOutlined, MailOutlined, CalendarOutlined, UserOutlined, CodeOutlined, BarChartOutlined } from "@ant-design/icons";
import { RiArrowGoBackFill } from "react-icons/ri";
import { PulseReportCreateForm } from "@components";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const PulseReportCreate = ({ history }) => {
  const [helpVisible, setHelpVisible] = useState(false);

  const reportTypesData = [
    { type: "invoice", description: "Billing & payment summary", bestFor: "After billing (on_refill)", includes: "Plan, amount, invoice PDF, payment method" },
    { type: "usage", description: "Chat & storage metrics", bestFor: "Weekly updates", includes: "Chats used/remaining, storage, trends" },
    { type: "performance", description: "Chatbot statistics", bestFor: "Weekly/monthly ROI", includes: "Conversations, duration, top bot, satisfaction" },
    { type: "analytics", description: "Traffic insights", bestFor: "Weekly digests", includes: "Visitors, sources, countries, devices" },
    { type: "digest", description: "Combined summary", bestFor: "Weekly/monthly", includes: "Multiple sections combined" },
    { type: "newsletter", description: "Announcements", bestFor: "Manual sends", includes: "Custom variables" },
    { type: "product_update", description: "Feature releases", bestFor: "Manual sends", includes: "Custom content" },
    { type: "custom", description: "Flexible reports", bestFor: "Any schedule", includes: "Mix & match sections" },
  ];

  const scheduleTypesData = [
    { type: "on_refill", when: "After billing cycle", delay: "1-24 hours configurable", bestFor: "Invoice reports" },
    { type: "weekly", when: "Specific day each week", delay: "Choose Mon-Sun + time", bestFor: "Usage, performance updates" },
    { type: "monthly", when: "Specific day each month", delay: "Choose day 1-28 + time", bestFor: "Monthly summaries" },
    { type: "quarterly", when: "Every 3 months", delay: "Jan, Apr, Jul, Oct", bestFor: "Business reviews" },
    { type: "manual", when: "Only when triggered", delay: "Click 'Send Now'", bestFor: "Newsletters, announcements" },
  ];

  const variablesData = [
    { category: "User", variables: "user_name, user_email, user_plan, user_joined_date", always: true },
    { category: "Billing", variables: "billing_plan, billing_amount, billing_currency, billing_period_start, billing_period_end, billing_next_renewal, billing_payment_method, billing_invoice_url, billing_pdf_url, billing_total_spent", always: false },
    { category: "Usage", variables: "usage_chats_allotted, usage_chats_used, usage_chats_remaining, usage_percentage, usage_storage_used, usage_storage_limit, usage_trend, usage_trend_icon", always: false },
    { category: "Performance", variables: "perf_total_chatbots, perf_active_chatbots, perf_total_conversations, perf_conversations_period, perf_avg_duration, perf_top_bot_name, perf_conversion_rate, perf_satisfaction, perf_trend", always: false },
    { category: "Analytics", variables: "analytics_total_visitors, analytics_unique_visitors, analytics_top_source_1/2/3, analytics_top_country_1/2/3, analytics_desktop_percent, analytics_mobile_percent, analytics_peak_hours", always: false },
    { category: "Team", variables: "team_member_count, team_active_members, team_tickets_handled, team_avg_response_time, team_top_member_1/2/3", always: false },
    { category: "Meta", variables: "report_generated_at, report_generated_time, report_type, report_name, current_year, current_month", always: true },
  ];

  return (
    <Card style={{ minHeight: "100vh", borderRadius: "0px" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Row align="middle">
            <RiArrowGoBackFill
              style={{ fontSize: "2em", cursor: "pointer" }}
              onClick={() => history.push("/super-admin/pulse-reports/list")}
            />
            <h3
              style={{
                marginLeft: "1em",
                fontSize: "1.75em",
                margin: "0 0 0 1em",
              }}
            >
              Create Pulse Report
            </h3>
          </Row>
        </Col>
        <Col>
          <Tooltip title="Help Guide">
            <QuestionCircleOutlined
              style={{
                fontSize: "1.25em",
                cursor: "pointer",
                color: "#722ed1",
              }}
              onClick={() => setHelpVisible(true)}
            />
          </Tooltip>
        </Col>
      </Row>

      <PulseReportCreateForm />

      {/* Help Drawer */}
      <Drawer
        title={
          <Space>
            <ThunderboltOutlined style={{ color: "#722ed1" }} />
            <span>Pulse Reports - Complete Guide</span>
          </Space>
        }
        placement="right"
        width={720}
        onClose={() => setHelpVisible(false)}
        open={helpVisible}
      >
        {/* What is Pulse Reports */}
        <Title level={4} style={{ color: "#722ed1" }}>
          <ThunderboltOutlined /> What is a Pulse Report?
        </Title>
        <Paragraph>
          <Text strong>Pulse Reports</Text> are automated, personalized email reports sent to users with their actual data.
          Unlike email campaigns (same content for everyone), each user receives <Text mark>unique content</Text> based on their:
        </Paragraph>
        <ul>
          <li>Billing information (plan, amount, invoices)</li>
          <li>Usage metrics (chats consumed, storage)</li>
          <li>Performance stats (conversations, satisfaction)</li>
          <li>Analytics data (visitors, traffic sources)</li>
        </ul>

        <Divider />

        {/* Report Types */}
        <Title level={4} style={{ color: "#52c41a" }}>
          <MailOutlined /> Report Types
        </Title>
        <Table
          dataSource={reportTypesData}
          columns={[
            { title: "Type", dataIndex: "type", render: (t) => <Tag color="purple">{t}</Tag> },
            { title: "Description", dataIndex: "description" },
            { title: "Best For", dataIndex: "bestFor" },
            { title: "Includes", dataIndex: "includes", width: 200 },
          ]}
          pagination={false}
          size="small"
          rowKey="type"
        />

        <Divider />

        {/* Schedule Types */}
        <Title level={4} style={{ color: "#fa8c16" }}>
          <CalendarOutlined /> Schedule Types
        </Title>
        <Table
          dataSource={scheduleTypesData}
          columns={[
            { title: "Type", dataIndex: "type", render: (t) => <Tag color="orange">{t}</Tag> },
            { title: "When", dataIndex: "when" },
            { title: "Configuration", dataIndex: "delay" },
            { title: "Best For", dataIndex: "bestFor" },
          ]}
          pagination={false}
          size="small"
          rowKey="type"
        />

        <Divider />

        {/* Target Audience */}
        <Title level={4} style={{ color: "#1890ff" }}>
          <UserOutlined /> Target Audience Options
        </Title>
        <Collapse size="small" ghost>
          <Panel header={<Text strong>All Users</Text>} key="all">
            <Paragraph>Send to every active user. You can still exclude specific users. Best for platform-wide announcements.</Paragraph>
          </Panel>
          <Panel header={<Text strong>Users by Plan</Text>} key="plan">
            <Paragraph>Target users on specific subscription plans (Free, Optimizer, Wizard, Maverick). Great for plan-specific features or upgrade prompts.</Paragraph>
          </Panel>
          <Panel header={<Text strong>Specific Users</Text>} key="targeted">
            <Paragraph>Hand-pick individual users by searching name/email. Perfect for VIP communications or testing.</Paragraph>
          </Panel>
          <Panel header={<Text strong>Users by Join Date</Text>} key="dateRange">
            <Paragraph>Target users who registered between specific dates. Great for cohort-based campaigns or onboarding.</Paragraph>
          </Panel>
          <Panel header={<Text strong>External Recipients</Text>} key="external">
            <Paragraph>
              Import non-registered emails via CSV/Excel. <Text mark>CSV columns become template variables!</Text>
              <br /><br />
              Example: If your CSV has columns <code>email, name, company</code>, use <code>{"{{name}}"}</code> and <code>{"{{company}}"}</code> in your template.
            </Paragraph>
          </Panel>
        </Collapse>

        <Divider />

        {/* Template Variables */}
        <Title level={4} style={{ color: "#13c2c2" }}>
          <CodeOutlined /> Template Variables
        </Title>
        <Paragraph>
          Use <code>{"{{variable_name}}"}</code> syntax in your HTML template and email subject. Variables are replaced with each user's actual data.
        </Paragraph>
        <Table
          dataSource={variablesData}
          columns={[
            {
              title: "Category",
              dataIndex: "category",
              render: (c, r) => (
                <Space>
                  <Tag color={r.always ? "green" : "blue"}>{c}</Tag>
                  {r.always && <Tag color="success">Always Available</Tag>}
                </Space>
              )
            },
            { title: "Variables", dataIndex: "variables", render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
          ]}
          pagination={false}
          size="small"
          rowKey="category"
        />

        <Divider />

        {/* Data Sections */}
        <Title level={4} style={{ color: "#eb2f96" }}>
          <BarChartOutlined /> Data Sections
        </Title>
        <Paragraph>
          Toggle which data to include in the report. <Text strong>Only enabled sections will have their variables populated.</Text>
        </Paragraph>
        <ul>
          <li><Text strong>Billing:</Text> Plan, amount, payment method, invoice links</li>
          <li><Text strong>Usage:</Text> Chats allotted/used/remaining, storage, trends</li>
          <li><Text strong>Performance:</Text> Chatbots, conversations, duration, satisfaction</li>
          <li><Text strong>Analytics:</Text> Visitors, sources, countries, devices</li>
          <li><Text strong>Team:</Text> Members, tickets, response times</li>
        </ul>

        <Divider />

        {/* Quick Start Guide */}
        <Title level={4} style={{ color: "#52c41a" }}>
          Quick Start Guide
        </Title>
        <ol>
          <li><Text strong>Choose Target:</Text> Select who receives the report</li>
          <li><Text strong>Name & Type:</Text> Give it a name and select report type</li>
          <li><Text strong>Select Template:</Text> Choose an HTML template with variables</li>
          <li><Text strong>Set Schedule:</Text> When should it be sent?</li>
          <li><Text strong>Toggle Data:</Text> Enable the data sections you need</li>
          <li><Text strong>Configure Email:</Text> Subject, from name, sending accounts</li>
          <li><Text strong>Save & Test:</Text> Save as draft, send a test email</li>
          <li><Text strong>Activate:</Text> When ready, activate the report</li>
        </ol>

        <Divider />

        {/* Pro Tips */}
        <Title level={4} style={{ color: "#faad14" }}>
          Pro Tips
        </Title>
        <ul>
          <li><Text strong>Invoice Reports:</Text> Use <code>on_refill</code> schedule with 6-hour delay</li>
          <li><Text strong>Subject Lines:</Text> Include variables like <code>{"{{user_name}}"}</code> for personalization</li>
          <li><Text strong>External Recipients:</Text> All CSV columns become usable variables</li>
          <li><Text strong>Testing:</Text> Always send a test email before activating</li>
          <li><Text strong>Monthly Reports:</Text> Use day 1 for start-of-month, day 28 for end</li>
        </ul>
      </Drawer>
    </Card>
  );
};

export default PulseReportCreate;
